import {
  DOWNSAMPLED_THRESHOLD,
  LOW_RESOLUTION_THRESHOLD
} from 'app/components/flowRateSampleIndicators/DataResolutionIndicators';
import { addCommas } from 'app/util/utils';

const kbUrl = 'https://kb.kentik.com/docs/flow-overview#flow-sampling';

/**
 * Rounds an FPS value for display purposes using Significant Figures calculation
 * - Values < 1 round to 1 significant figure (e.g. 0.91 → 0.9, 0.495 → 0.5)
 * - Values >= 1 and < 10 round to 2 significant figures (e.g. 1.0 → 1.0, 1.49 → 1.5, 9.432 → 9.4)
 * - Values >= 10 are rounded to the nearest integer (e.g. 73.4 → 73, 11.512 → 12, 123.456 → 123, 1234.5 → 1,235)
 *
 * This prevents smaller values from being misleadingly rendered to users.
 * Example:
 * A device with 1.49 received FPS and 0.51 stored FPS is downsampled at a rate of nearly 1:3,
 * but would display as `1 FPS` if both values are rounded to the nearest integer (1.49 → 1, 0.51 → 1).
 */
export function formatFpsValueForDisplay(fps: number): string {
  switch (true) {
    case fps < 1:
      return addCommas(fps.toPrecision(1));
    case fps < 10:
      return addCommas(fps.toPrecision(2));
    default:
      return addCommas(Math.round(fps));
  }
}

/**
 * Determine the display text for downsampling severity based on sample rate
 */
export function getDownsamplingSeverity(sampleRate: number): string | null {
  if (!sampleRate || sampleRate <= DOWNSAMPLED_THRESHOLD) {
    return null;
  }

  return sampleRate >= LOW_RESOLUTION_THRESHOLD ? 'Heavily Downsampled' : 'Downsampled';
}

const generalDownsamplingContext = `
# Flow Data Downsampling
Flow rate "downsampling" occurs when a flow device does not send all of it's received flow data to Kentik's Ingest layer to be processed.
A device with a flow sample rate of 1:5 is only sending Kentik 1 in 5 (20%) of the flow packets it receives.

### Causes of Downsampling
- A device's configured sample rate is set higher than 1:1.
- The device's plan does not have enough FPS to handle the amount of flow data received by devices in that plan.
- Downsampling IS NOT caused by the volume of flow data exceeding Kentik's Ingest layer's capacity to process it in real-time.
Kentik is able to process any amount of flow in real-time.
Downsampling is caused by issues with the customer's device configuration or plan capacity, not by limitations in Kentik's processing capabilities.

To manage these problems, Kentik automatically downsamples the flow data for the customer to prevent problems caused by
processing more flow than the devices/plan are configured for, only processing a fraction of the total flows.
This does not prevent downsampling from impacting data accuracy and granularity, and users should still troubleshoot the underlying issue to restore full resolution data.

### Downsampling Thresholds and Severity Levels
- Sampling ratios greater than 1:1 and less than 1:5 are considered moderate downsampling
- Sampling ratios greater than 1:5 are considered low resolution (heavily downsampled)
- A sampling ration of 1:1 is full resolution (not downsampled)

### Downsampling Impacts
- Reduced data accuracy and granularity
- Inability to see low-volume flows or fine-grained traffic patterns
- Inaccurate reporting and observability insights
`;

const troubleshootingContext = `
**Troubleshooting:**
- Contact your Kentik Network Intelligence Specialist
- Review your plan's flow capacity to ensure it is high enough for your devices' received flow volume
- Check flow rate metrics on the Device Telemetry tab
- Review devices' configured flow sample rates
- Refer to the knowledge base for optimization strategies: ${kbUrl}
`;

interface DeviceDownsamplingInfo {
  name: string;
  sampleRate: number;
}

/**
 * Format multiple devices' downsampling data as CSV for AIA context
 */
export function formatDevicesDownsamplingContext(devices: DeviceDownsamplingInfo[]): string {
  // Filter and map downsampled devices
  const downsampledDevices = devices
    .filter((device) => {
      const { sampleRate } = device;
      return sampleRate && sampleRate > DOWNSAMPLED_THRESHOLD;
    })
    .map((device) => {
      const { name, sampleRate } = device;
      const severity = getDownsamplingSeverity(sampleRate);
      const deviceUrl = `/v4/infrastructure/devices/${encodeURIComponent(name)}/telemetry`;

      return {
        name,
        sampleRate: `1:${sampleRate}`,
        severity,
        url: deviceUrl
      };
    })
    .sort((a, b) => {
      // Sort by severity first (heavily downsampled first), then by name
      if (a.severity !== b.severity) {
        return a.severity === 'Heavily Downsampled' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });

  // Return empty string if no downsampled devices
  if (downsampledDevices.length === 0) {
    return '';
  }

  // Format as CSV-style output
  const csvHeader = 'Device Name; Sample Rate; Severity; Device URL';
  const csvRows = downsampledDevices.map((d) => `${d.name}; ${d.sampleRate}; ${d.severity}; ${d.url}`).join('\n');

  const contextText = `
${generalDownsamplingContext}

The section below identifies devices being downsampled at our Ingest layer. This can affect data accuracy and granularity.

If ANY devices are present in the CSV table below, you MUST include it in your response to ensure the user is aware of potential data limitations.
Render the CSV table exactly as it appears below, with the headers ${csvHeader}.
If the table contains more than 20 rows, render a "Downsampling Summary" table with these columns: "Count, Severity, Sample Rate Range", with one row per severity level
that summarizes the number of devices in each severity level and the range of sample rates for those devices.
Then use the CSV table below for only the 20 devices with the highest rates of downsampling.
Also include ${kbUrl} in your response so users can read more about flow sampling. Users can click device URLs in the CSV table to view detailed telemetry and investigate flow rates.

Where possible, look at the configured sample rate and plan FPS capacity for downsampled devices, and make intelligent recommendations about the likely cause of downsampling.
If you're not sure, don't guess or make up an answer, just state that it's unclear and recommend the user investigate the device's configured sample rate and plan FPS capacity as potential causes.

--csv
${csvHeader}
${csvRows}
--csv

${troubleshootingContext}
`;
  return contextText;
}

/**
 * Format a single device's downsampling data as narrative text for AI context
 */
export function formatSingleDeviceDownsamplingContext(deviceName: string, flowSampleRate: number): string {
  const severity = getDownsamplingSeverity(flowSampleRate);

  // Return empty if not downsampled
  if (!severity) {
    return '';
  }

  const isHeavy = flowSampleRate >= LOW_RESOLUTION_THRESHOLD;
  const severityLabel = isHeavy ? 'Heavily Downsampled (Low Resolution)' : 'Moderately Downsampled';

  const contextText = `
${generalDownsamplingContext}

# Flow Data Downsampling Alert

⚠️ Device ${deviceName} is currently experiencing flow data downsampling at the Ingest layer.

- **Flow Sample Rate:** 1:${flowSampleRate} (sampling 1 in ${flowSampleRate} flows)
- **Severity:** ${severityLabel}
- **Impact:** Data accuracy and granularity may be affected
- **Threshold:** Ratios up to 1:5 are moderate, above 1:5 are considered low resolution

Where possible, don't just say that the Device is downsampled, but rather look at that device's configured sample rate and the device's plan max FPS and per-device FPS allocation,
and make intelligent recommendations about the likely cause of downsampling. If you're not sure, don't guess or make up an answer,
just state that it's unclear and recommend the user investigate the device's configured sample rate and plan FPS capacity as potential causes.

${troubleshootingContext}
`;

  return contextText;
}
